// 치과 「접수」(데스크 QR 대기 순서) 서버 호출 (2026-10-03) — 서버(callable)는 dev-server 담당. region 은 clinicWalkinApi.js 관례(asia-northeast3)
// 오류 규약: 서버 오류는 모두 HttpsError + details.code. details 없는 not-found/internal = 서버 미배포 → 화면 "접수 서버 준비 중"
//   (판정: utils/deskReception.js isReceptionServerNotReady). 플래그 config/features.deskReception OFF → failed-precondition {code:'feature-off'}
//
// 공개(비로그인):
//  - submitDeskReception({ slug, kind:'old'|'new', name, reason, birth?, sex?, tel?, addr?, consent:{ version, textHash, ageMode, age14Checked?, guardian? } }) → { ticket, order }
//      consent(10-04 추가 계약): ageMode 'self14' = age14Checked:true 필수·guardian 금지 / 'guardian' = guardian:{ name, relation:'father'|'mother'|'guardian' } 필수·보호자 판
//      kind='old' 이면 birth·sex·tel·addr 를 보내지 않는다. 응답은 기존 등록 여부와 무관하게 항상 같은 모양
//  - getReceptionTicket({ slug, ticket }) → { order, called, calledAt, closed }
// 치과(대표·직원):
//  - listTodayReception() → { items:[{ id, kind, name, reason, at, status:'wait'|'called', calledAt, orderIds?, guardian, source:'desk'|'qr', desk }] }
//      desk:true(데스크 직접 추가) = kind:null · guardian:false · reason null 가능 · 상세 카드 없음(getReceptionDetail 호출 금지)
//  - addDeskWalkin({ name, reason? }) → { id, order }   대표·직원. 그날 대기 맨 뒤, 자정 마감 뒤 삭제. 처리위탁 확인 1.2 치과만(desk-add-consent-required)
//  - removeDeskWalkin({ id }) → { ok }   대표·직원. 오늘·데스크 추가 건만(QR 건 = not-desk-entry)
//  - callReception({ id }) → { ok, calledAt }
//  - getReceptionDetail({ id }) → { card:{ name, birth, sex, tel, addr, reason, at, date, guardian:{ name, relation }|null } }   접속기록(누가·언제) 남는 경로
// 치과 처리위탁 확인(플래그 무관):
//  - getDeskClinicConsent() → { confirmed, version, at, current, currentTextHash, isCurrent, canConfirm(대표만), deskAddReady, deskAddVersion, deskAddFrom }
//      1.1 확인 치과 = confirmed:true·isCurrent:false·deskAddReady:false (QR 접수는 그대로, 직접 추가만 1.2 필요)
//  - acceptDeskClinicConsent({ version, textHash }) → { ok, version, already }   직원 = permission-denied owner-only
//  - listReceptionMonth({ ym:'YYYY-MM' }) → { days:{ 'YYYY-MM-DD':{ total, newCount } } }
//  - listReceptionDay({ date:'YYYY-MM-DD' }) → { items:[{ id, kind, name, reason, at, orderIds? }] }
//  - searchReceptionHistory({ name }) → { items:[{ id, date, kind, name, reason, at, orderIds? }] }   전체 날짜(2년 보관분)
//  - linkOrderToReception({ orderId, receptionId? }) → { linked:'auto'|'picked'|'none', candidates?:[{ id, name, kind, at }] }   데스크 추가 건 지정 = reception-not-linkable
//  - unlinkOrderReception({ orderId }) → { ok }
//  - deleteReceptionEntry({ id, reason }) → { ok }   대표만(owner-only), reason ∈ patient_request|wrong_entry|spam|other
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';

const REGION = 'asia-northeast3';
const call = (name) => httpsCallable(getFunctions(undefined, REGION), name);
const data = (r) => r?.data || {};

// 동의 — 계약 consent 전체를 보낸다(종전 {version,textHash}만 다시 만들어 age14Checked 를 버리던 결함 수정, 10-04).
//   self14: age14Checked 는 true 일 때만 싣는다(서버가 true 아니면 consent-required). guardian 키는 보내지 않는다(서버 field-not-allowed)
//   guardian: guardian{name,relation} 만 — age14Checked 는 싣지 않는다
function buildConsent(c) {
    const ageMode = c?.ageMode === 'guardian' ? 'guardian' : 'self14';
    const out = { version: String(c?.version || ''), textHash: String(c?.textHash || ''), ageMode };
    if (ageMode === 'self14') {
        if (c?.age14Checked === true) out.age14Checked = true;
    } else {
        out.guardian = {
            name: String(c?.guardian?.name || '').normalize('NFC').trim(),
            relation: String(c?.guardian?.relation || ''),
        };
    }
    return out;
}

// 허용 키만 보낸다 — 처음 온 환자 항목은 kind==='new' 일 때만
export async function submitDeskReception(p) {
    const body = {
        slug: String(p?.slug || ''),
        kind: p?.kind === 'new' ? 'new' : 'old',
        name: String(p?.name || '').normalize('NFC').trim(),
        reason: String(p?.reason || ''),
        consent: buildConsent(p?.consent),
    };
    if (body.kind === 'new') {
        body.birth = String(p?.birth || '');
        body.sex = String(p?.sex || '');
        body.tel = String(p?.tel || '');
        body.addr = String(p?.addr || '').trim();
    }
    return data(await call('submitDeskReception')(body));
}

export async function getReceptionTicket(slug, ticket) {
    return data(await call('getReceptionTicket')({ slug: String(slug || ''), ticket: String(ticket || '') }));
}

// opts: 화면 진입·수동 새로고침 때만 { log: true, client: 'web' } — 서버가 목록 열람 접속기록 1건(법적보안 P1-3, 2026-10-12).
//   30초 폴링·동작 뒤 재조회는 인자 없이 부른다(기록 안 쌓임)
export async function listTodayReception(opts) {
    return data(await call('listTodayReception')(opts || {}));
}

// 데스크 직접 추가 (2026-10-04 기획서_접수_수동추가) — 허용 키 name·reason 만(다른 키는 서버 field-not-allowed). reason 미선택이면 보내지 않는다
export async function addDeskWalkin(name, reason) {
    const body = { name: String(name || '').normalize('NFC').trim() };
    if (reason) body.reason = String(reason);
    return data(await call('addDeskWalkin')(body));
}

export async function removeDeskWalkin(id) {
    return data(await call('removeDeskWalkin')({ id: String(id || '') }));
}

export async function callReception(id) {
    return data(await call('callReception')({ id: String(id || '') }));
}

export async function getReceptionDetail(id) {
    return data(await call('getReceptionDetail')({ id: String(id || '') }));
}

export async function listReceptionMonth(ym) {
    return data(await call('listReceptionMonth')({ ym: String(ym || '') }));
}

export async function listReceptionDay(date) {
    return data(await call('listReceptionDay')({ date: String(date || '') }));
}

export async function searchReceptionHistory(name) {
    return data(await call('searchReceptionHistory')({ name: String(name || '').normalize('NFC').trim() }));
}

// 의뢰서 ↔ 오늘 접수 연결 (2026-10-03 대표 추가 지시) — receptionId 없으면 서버가 같은 날(KST)·이름 완전 일치로 자동 판정
export async function linkOrderToReception(orderId, receptionId) {
    const body = { orderId: String(orderId || '') };
    if (receptionId) body.receptionId = String(receptionId);
    return data(await call('linkOrderToReception')(body));
}

// 접수 1건 삭제 (10-04 추가 계약 — 처리방침 "삭제 요청 시 삭제" 수단). 치과 대표만(직원 = permission-denied owner-only), 없으면 not-found reception-not-found
//   reason ∈ patient_request | wrong_entry | spam | other  → { ok:true }
export async function deleteReceptionEntry(id, reason) {
    return data(await call('deleteReceptionEntry')({ id: String(id || ''), reason: String(reason || '') }));
}

export async function unlinkOrderReception(orderId) {
    return data(await call('unlinkOrderReception')({ orderId: String(orderId || '') }));
}

// 치과 「데스크 접수 처리위탁 확인」 (10-04 추가 계약) — 대표만 확인 가능, 직원은 조회만
export async function getDeskClinicConsent() {
    const r = data(await call('getDeskClinicConsent')({}));
    rememberClinicConsent(r?.confirmed === true);
    return r;
}

// 처리위탁 확인 여부 세션 캐시 (10-04 검토 P2) — 접수를 쓰지 않는 치과의 의뢰서 저장마다 linkOrderToReception 을 부르지 않으려고.
//   메모리(탭 수명)만 — sessionStorage 에 쓰지 않는다(파일 상단 원칙: 저장소에는 ticket 만). 계정이 바뀌면 다시 묻는다.
//   확인됨=탭 수명 동안 유지 / 미확인=5분 뒤 다시 확인(대표가 그 사이 확인했을 수 있음). getDeskClinicConsent 호출마다 갱신.
const CONSENT_FALSE_TTL_MS = 5 * 60 * 1000;
let consentCache = null;   // { uid, confirmed, at }
function currentUid() {
    try { return getAuth().currentUser?.uid || ''; }
    catch (e) { console.error('[접수] 로그인 상태 확인 실패:', e?.code || e?.message || e); return ''; }
}
function rememberClinicConsent(confirmed) {
    const uid = currentUid();
    if (uid) consentCache = { uid, confirmed, at: Date.now() };
}
export async function isDeskClinicConsentConfirmedCached() {
    const uid = currentUid();
    const c = consentCache;
    if (uid && c && c.uid === uid && (c.confirmed || Date.now() - c.at < CONSENT_FALSE_TTL_MS)) return c.confirmed;
    const r = await getDeskClinicConsent();
    return r?.confirmed === true;
}

export async function acceptDeskClinicConsent(version, textHash) {
    return data(await call('acceptDeskClinicConsent')({ version: String(version || ''), textHash: String(textHash || '') }));
}
